import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import { getThemeById } from '../config/themes'
import {
  getPromotionDashboard,
  applyPromotion,
  type PromotionCampaignItem,
} from '../services/promotionService'
import {
  getBirthdayDisplayPromotionConfig,
  getCashbackDisplayPromotionConfig,
  getFacebookShareDisplayPromotionConfig,
  getMonthlyRewardPromotionConfig,
  getPromotionHubDisplayPromotionConfig,
  getReferralDisplayPromotionConfig,
  type UserBirthdayDisplayPromotionConfig,
  type UserCashbackDisplayPromotionConfig,
  type UserCashbackDisplaySettings,
  type UserFacebookShareDisplayPromotionConfig,
  type UserManualReviewBonusDisplaySettings,
  type UserMonthlyRewardDisplaySettings,
  type UserMonthlyRewardPromotionConfig,
  type UserPromotionHubDisplayPromotionConfig,
  type UserPromotionHubDisplaySettings,
  type UserPromotionHubManualCard,
  type UserPromotionHubSectionSettings,
  type UserPromotionHubShortcutSettings,
  type UserReferralDisplayPromotionConfig,
  type UserReferralDisplaySettings,
} from '../services/promotionConfigService'
import './ModernPromotionPage.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernPromotionPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const IMG_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

type CampaignCardItem = {
  id: string
  title: string
  description: string
  img: string
  buttonText: string
  campaignId: string
  actionType?: 'campaign' | 'route'
  actionValue?: string
}

type ShortcutCardItem = UserPromotionHubShortcutSettings & {
  imageUrl?: string
  imageAlt?: string
}

const DEFAULT_REFERRAL_DISPLAY_SETTINGS: UserReferralDisplaySettings = {
  pageTitle: 'แนะนำเพื่อน',
  pageSubtitle: 'แชร์ลิงก์ของคุณเพื่อรับค่าคอมมิชชั่นจากยอดเล่นของสมาชิกที่คุณแนะนำ',
  withdrawButtonText: 'ถอนคอมมิชชั่น',
  minCommissionWithdrawalAmount: 100,
  shareSectionTitle: 'ลิงก์แนะนำ',
  linkLabel: 'ลิงก์สำหรับโปรโมท',
  copyButtonText: 'คัดลอก',
  shareDescription:
    'ลิงก์ช่วยแชร์รับ {{rate}} ฟรี (แค่ก๊อปปี้ลิงก์ไปแชร์ก็ได้เงินแล้ว) ยิ่งแชร์มากยิ่งได้มาก ท่านสามารถนำลิงก์ด้านล่างนี้ไปแชร์ในช่องทางต่างๆ ไม่ว่าจะเป็น เว็บไซต์ส่วนตัว, Blog, Facebook หรือ Social Network อื่นๆ หากมีการสมัครสมาชิกโดยคลิกผ่านลิงก์ของท่านเข้ามา ลูกค้าที่สมัครเข้ามาก็จะอยู่ภายใต้เครือข่ายของท่านทันที และหากลูกค้าภายใต้เครือข่ายของท่านมีการเดิมพันเข้ามา ทุกยอดการเดิมพันท่านจะได้รับส่วนแบ่งในการแนะนำ {{rate}} ทันทีโดยไม่มีเงื่อนไข',
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบแนะนำเพื่อนปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  historyEmptyText: 'ยังไม่พบรายการ',
  hideZeroCommissionSegments: true,
  promotionRoute: '/affiliate',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'แนะนำเพื่อน',
    badgeText: 'Referral',
    title: 'แนะนำเพื่อน',
    description: 'ดูคอมมิชชั่น ลิงก์โปรโมท และประวัติการแนะนำเพื่อน',
    buttonText: 'ไปหน้าแนะนำเพื่อน',
  },
}

const DEFAULT_CASHBACK_DISPLAY_SETTINGS: UserCashbackDisplaySettings = {
  pageTitle: 'เงินคืนยอดเล่น',
  pageSubtitle: 'ตรวจสอบยอดคืนล่าสุด กดรับ cashback ได้ทันที และดูเงื่อนไขได้ในหน้าจอเดียว',
  heroBadgeText: 'Cashback Center',
  withdrawalButtonText: 'ถอนเงินแนะนำ',
  noticeText: 'รายได้การคืนยอดเล่นของท่านสามารถแจ้งถอนได้ทุกเวลา หากมียอดรายได้มากกว่า 15 บาทขึ้นไป',
  minWithdrawalAmount: 15,
  cashbackRateText: '0.3% - 0.8%',
  promotionalMessage: 'สุดยอดโปรโมชั่น รับเงินคืนฟรี ทันทีทุกวัน ทั้งวัน ไม่อั้น ไม่ติดเทิร์น ง่ายๆ',
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบ cashback ปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  historyEmptyText: 'ยังไม่มีประวัติ cashback',
  showRateTable: true,
  showExample: true,
  promotionRoute: '/cashback',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'คืนยอดเสีย',
    badgeText: 'Cashback',
    title: 'คืนยอดเสีย',
    description: 'ดูยอด cashback ล่าสุด กดรับสิทธิ์ และดูประวัติการรับเงินคืน',
    buttonText: 'ไปหน้า Cashback',
  },
}

const DEFAULT_BIRTHDAY_DISPLAY_SETTINGS: UserManualReviewBonusDisplaySettings = {
  pageTitle: 'โบนัสวันเกิด',
  pageSubtitle: 'ส่งหลักฐานยืนยันวันเกิดให้แอดมินตรวจสอบก่อนอนุมัติรับสิทธิ์โบนัส',
  contactSectionTitle: 'เอกสารที่ต้องส่งให้แอดมิน',
  contactSectionSubtitle: 'กรุณาส่งหลักฐานให้ครบถ้วนเพื่อให้ทีมงานตรวจสอบและอนุมัติได้รวดเร็ว',
  requirements: [
    'ส่งรูปบัตรประชาชนที่เห็นชื่อและวันเดือนปีเกิดชัดเจน',
    'แจ้ง username หรือเบอร์โทรที่ใช้สมัครสมาชิก',
    'รอแอดมินตรวจสอบความถูกต้องก่อนรับโบนัส',
  ],
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบโบนัสวันเกิดปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'โบนัสวันเกิด',
    badgeText: 'Birthday',
    title: 'โบนัสวันเกิด',
    description: 'ส่งรูปบัตรประชาชนให้แอดมินตรวจสอบ เพื่อรับสิทธิ์โบนัสวันเกิดตามเงื่อนไขของเว็บไซต์',
    buttonText: 'ติดต่อแอดมิน',
  },
}

const DEFAULT_FACEBOOK_SHARE_DISPLAY_SETTINGS: UserManualReviewBonusDisplaySettings = {
  pageTitle: 'โบนัสแชร์เฟส',
  pageSubtitle: 'ส่งภาพโพสต์และลิงก์ที่แชร์ให้แอดมินตรวจสอบก่อนอนุมัติรับสิทธิ์โบนัส',
  contactSectionTitle: 'หลักฐานที่ต้องส่งให้แอดมิน',
  contactSectionSubtitle: 'กรุณาส่งข้อมูลการแชร์ให้ครบถ้วนเพื่อให้ทีมงานตรวจสอบได้ทันที',
  requirements: [
    'แคปภาพโพสต์ Facebook ที่แชร์แล้วให้เห็นชัดเจน',
    'ส่ง URL ของโพสต์ที่แชร์เพื่อให้แอดมินเปิดตรวจสอบได้',
    'แจ้ง username หรือเบอร์โทรที่ใช้สมัครสมาชิก',
  ],
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบโบนัสแชร์เฟสปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'โบนัสแชร์เฟส',
    badgeText: 'Facebook',
    title: 'โบนัสแชร์เฟส',
    description: 'แคปภาพโพสต์และส่ง URL ที่แชร์ให้แอดมินตรวจสอบก่อนอนุมัติรับโบนัส',
    buttonText: 'ติดต่อแอดมิน',
  },
}

const DEFAULT_BIRTHDAY_DISPLAY_CONFIG: UserBirthdayDisplayPromotionConfig = {
  promotionType: 'birthday_display',
  enabled: true,
  settings: DEFAULT_BIRTHDAY_DISPLAY_SETTINGS,
}

const DEFAULT_FACEBOOK_SHARE_DISPLAY_CONFIG: UserFacebookShareDisplayPromotionConfig = {
  promotionType: 'facebook_share_display',
  enabled: true,
  settings: DEFAULT_FACEBOOK_SHARE_DISPLAY_SETTINGS,
}

const DEFAULT_MONTHLY_REWARD_CONFIG: UserMonthlyRewardPromotionConfig = {
  promotionType: 'monthly_reward',
  enabled: true,
  settings: {
    display: {
      pageTitle: 'รางวัลประจำเดือน',
      subtitle: 'อัปเดตอันดับเดือนปัจจุบันแบบ live และเติมข้อมูลจำลองให้น่าเชื่อถือเมื่อข้อมูลจริงยังไม่ครบ',
      rowsPerTab: 10,
      promotionCard: {
        imageUrl: '',
        imageAlt: 'รางวัลประจำเดือน',
        badgeText: 'Reward',
        title: 'รางวัลประจำเดือน',
        description: 'ติดตามอันดับล่าสุดและสิทธิ์รับรางวัลประจำเดือนของสมาชิก',
        buttonText: 'ไปหน้ารางวัล',
        route: '/reward',
      },
      disabledTitle: 'ปิดใช้งานชั่วคราว',
      disabledMessage: 'ระบบรางวัลประจำเดือนปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
      promotionRoute: '/reward',
    },
  },
}

const DEFAULT_SHORTCUT = (
  sortOrder: number,
  badgeText: string,
  title: string,
  description: string,
  buttonText: string,
  route: string
): UserPromotionHubShortcutSettings => ({
  enabled: true,
  sortOrder,
  badgeText,
  title,
  description,
  buttonText,
  route,
})

const DEFAULT_MANUAL_CARD = (sortOrder: number): UserPromotionHubManualCard => ({
  id: `promotion-card-${sortOrder}`,
  enabled: true,
  sortOrder,
  badgeText: '',
  title: '',
  description: '',
  imageUrl: '',
  imageAlt: '',
  buttonText: 'ดูรายละเอียด',
  actionType: 'none',
  actionValue: '',
  openInNewTab: false,
  startsAt: null,
  endsAt: null,
})

const DEFAULT_PROMOTION_HUB_SETTINGS: UserPromotionHubDisplaySettings = {
  pageTitle: 'โปรโมชั่น',
  pageSubtitle: 'รวมโบนัสและโปรโมชันเด่นที่จัดวางด้วยภาพให้ลูกค้าเห็นแล้วอยากกดเข้าร่วมทันที',
  heroBadgeText: 'Promotion Hub',
  warningTitle: 'คำเตือน',
  warningMessagePrimary: 'ทุกโบนัสต้องกดรับหลังจากฝากเงินเท่านั้น ห้ามเล่นก่อนเด็ดขาด',
  warningMessageSecondary: 'หากทีมงานตรวจสอบว่ามีการรับโบนัสเพื่อแทงสวน ทางทีมงานจะไม่ทำรายการถอนให้ทุกกรณี',
  featuredSectionTitle: 'โปรโมชันแนะนำ',
  featuredSectionSubtitle: 'โปรโมชันแบบภาพที่ช่วยสื่อสารแคมเปญเด่นของเว็บได้ชัดเจนยิ่งขึ้น',
  campaignsSectionTitle: 'โบนัสที่เปิดรับอยู่',
  campaignsSectionSubtitle: 'รวมโบนัสที่กดรับได้ทันที และโบนัสที่ต้องส่งหลักฐานให้แอดมินตรวจสอบจากหน้าเดียว',
  emptyCampaignText: 'ขณะนี้ยังไม่มีโบนัสที่เปิดรับในระบบ',
  receiveButtonText: 'รับโบนัส',
  showCampaigns: true,
  disabledTitle: 'ปิดใช้งานหน้ารวมโปรโมชั่นชั่วคราว',
  disabledMessage: 'ขณะนี้ระบบปิดหน้าโปรโมชั่น กรุณาติดต่อแอดมินหากต้องการข้อมูลเพิ่มเติม',
  sections: {
    warning: { enabled: true, sortOrder: 1 },
    featuredPromotions: { enabled: true, sortOrder: 2 },
    campaigns: { enabled: true, sortOrder: 3 },
    shortcuts: { enabled: true, sortOrder: 4 },
  },
  manualCards: [],
  shortcuts: {
    cashback: DEFAULT_SHORTCUT(
      1,
      'Cashback',
      'เครดิตเงินคืน',
      'ดูยอด cashback ล่าสุด กดรับสิทธิ์ และดูประวัติการรับเงินคืน',
      'ไปหน้า Cashback',
      '/cashback'
    ),
    monthlyReward: DEFAULT_SHORTCUT(
      2,
      'Reward',
      'รางวัลประจำเดือน',
      'ติดตามอันดับล่าสุดและสิทธิ์รับรางวัลประจำเดือนของสมาชิก',
      'ไปหน้ารางวัล',
      '/reward'
    ),
    referral: DEFAULT_SHORTCUT(
      3,
      'Referral',
      'แนะนำเพื่อน',
      'ดูคอมมิชชั่น ลิงก์โปรโมท และประวัติการแนะนำเพื่อน',
      'ไปหน้าแนะนำเพื่อน',
      '/affiliate'
    ),
    birthday: DEFAULT_SHORTCUT(
      4,
      'Birthday',
      'โบนัสวันเกิด',
      'ส่งรูปบัตรประชาชนให้แอดมินตรวจสอบ เพื่อรับสิทธิ์โบนัสวันเกิดตามเงื่อนไขของเว็บไซต์',
      'ติดต่อแอดมิน',
      '/contact?topic=birthday-bonus'
    ),
    facebookShare: DEFAULT_SHORTCUT(
      5,
      'Facebook',
      'โบนัสแชร์เฟส',
      'แคปภาพโพสต์และส่ง URL ที่แชร์ให้แอดมินตรวจสอบก่อนอนุมัติรับโบนัส',
      'ติดต่อแอดมิน',
      '/contact?topic=facebook-share-bonus'
    ),
  },
}

function normalizeShortcut(
  shortcut: Partial<UserPromotionHubShortcutSettings> | undefined,
  fallback: UserPromotionHubShortcutSettings
): UserPromotionHubShortcutSettings {
  return {
    enabled: typeof shortcut?.enabled === 'boolean' ? shortcut.enabled : fallback.enabled,
    sortOrder:
      typeof shortcut?.sortOrder === 'number' && Number.isFinite(shortcut.sortOrder)
        ? shortcut.sortOrder
        : fallback.sortOrder,
    badgeText: typeof shortcut?.badgeText === 'string' && shortcut.badgeText.trim() ? shortcut.badgeText : fallback.badgeText,
    title: typeof shortcut?.title === 'string' && shortcut.title.trim() ? shortcut.title : fallback.title,
    description:
      typeof shortcut?.description === 'string' && shortcut.description.trim()
        ? shortcut.description
        : fallback.description,
    buttonText:
      typeof shortcut?.buttonText === 'string' && shortcut.buttonText.trim()
        ? shortcut.buttonText
        : fallback.buttonText,
    route: typeof shortcut?.route === 'string' && shortcut.route.trim() ? shortcut.route : fallback.route,
  }
}

function normalizeSection(
  section: Partial<UserPromotionHubSectionSettings> | undefined,
  fallback: UserPromotionHubSectionSettings
): UserPromotionHubSectionSettings {
  return {
    enabled: typeof section?.enabled === 'boolean' ? section.enabled : fallback.enabled,
    sortOrder:
      typeof section?.sortOrder === 'number' && Number.isFinite(section.sortOrder)
        ? section.sortOrder
        : fallback.sortOrder,
  }
}

function normalizeManualCard(
  card: Partial<UserPromotionHubManualCard> | undefined,
  fallback: UserPromotionHubManualCard,
  index: number
): UserPromotionHubManualCard {
  const normalizeDate = (value: unknown): string | null => {
    if (typeof value !== 'string' || !value.trim()) return null
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
  }

  return {
    id: typeof card?.id === 'string' && card.id.trim() ? card.id : fallback.id || `promotion-card-${index + 1}`,
    enabled: typeof card?.enabled === 'boolean' ? card.enabled : fallback.enabled,
    sortOrder:
      typeof card?.sortOrder === 'number' && Number.isFinite(card.sortOrder)
        ? card.sortOrder
        : fallback.sortOrder,
    badgeText: typeof card?.badgeText === 'string' ? card.badgeText : fallback.badgeText,
    title: typeof card?.title === 'string' ? card.title : fallback.title,
    description: typeof card?.description === 'string' ? card.description : fallback.description,
    imageUrl: typeof card?.imageUrl === 'string' ? card.imageUrl : fallback.imageUrl,
    imageAlt: typeof card?.imageAlt === 'string' ? card.imageAlt : fallback.imageAlt,
    buttonText: typeof card?.buttonText === 'string' && card.buttonText.trim() ? card.buttonText : fallback.buttonText,
    actionType:
      card?.actionType === 'campaign' || card?.actionType === 'route' || card?.actionType === 'url'
        ? card.actionType
        : 'none',
    actionValue: typeof card?.actionValue === 'string' ? card.actionValue : fallback.actionValue,
    openInNewTab: typeof card?.openInNewTab === 'boolean' ? card.openInNewTab : fallback.openInNewTab,
    startsAt: normalizeDate(card?.startsAt) ?? fallback.startsAt,
    endsAt: normalizeDate(card?.endsAt) ?? fallback.endsAt,
  }
}

function normalizeReferralDisplayConfig(
  config?: UserReferralDisplayPromotionConfig | null
): UserReferralDisplayPromotionConfig {
  const settings = config?.settings || {}
  const promotionCard = settings.promotionCard || {}

  return {
    promotionType: 'referral_display',
    enabled: config?.enabled !== false,
    settings: {
      ...DEFAULT_REFERRAL_DISPLAY_SETTINGS,
      ...settings,
      promotionCard: {
        ...DEFAULT_REFERRAL_DISPLAY_SETTINGS.promotionCard,
        ...(promotionCard || {}),
      },
    },
    updatedAt: config?.updatedAt ?? null,
    updatedBy: config?.updatedBy ?? null,
  }
}

function normalizeCashbackDisplayConfig(
  config?: UserCashbackDisplayPromotionConfig | null
): UserCashbackDisplayPromotionConfig {
  const settings = config?.settings || {}
  const promotionCard = settings.promotionCard || {}

  return {
    promotionType: 'cashback_display',
    enabled: config?.enabled !== false,
    settings: {
      ...DEFAULT_CASHBACK_DISPLAY_SETTINGS,
      ...settings,
      promotionCard: {
        ...DEFAULT_CASHBACK_DISPLAY_SETTINGS.promotionCard,
        ...(promotionCard || {}),
      },
    },
    updatedAt: config?.updatedAt ?? null,
    updatedBy: config?.updatedBy ?? null,
  }
}

function normalizeMonthlyRewardConfig(
  config?: UserMonthlyRewardPromotionConfig | null
): UserMonthlyRewardPromotionConfig {
  const defaultDisplay = DEFAULT_MONTHLY_REWARD_CONFIG.settings?.display as UserMonthlyRewardDisplaySettings
  const display = config?.settings?.display || {}
  const promotionCard = display.promotionCard || {}

  return {
    promotionType: 'monthly_reward',
    enabled: config?.enabled !== false,
    settings: {
      display: {
        ...defaultDisplay,
        ...display,
        promotionCard: {
          ...defaultDisplay.promotionCard,
          ...(promotionCard || {}),
        },
      },
    },
    updatedAt: config?.updatedAt ?? null,
    updatedBy: config?.updatedBy ?? null,
  }
}

function normalizeManualReviewBonusDisplayConfig<TConfig extends { enabled: boolean; settings?: Partial<UserManualReviewBonusDisplaySettings> }>(
  config: TConfig | null | undefined,
  defaults: UserManualReviewBonusDisplaySettings
): TConfig {
  const settings = config?.settings || {}
  const promotionCard = settings.promotionCard || {}

  return {
    ...(config || {}),
    enabled: config?.enabled !== false,
    settings: {
      ...defaults,
      ...settings,
      requirements:
        Array.isArray(settings.requirements) && settings.requirements.length > 0
          ? settings.requirements.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
          : defaults.requirements,
      promotionCard: {
        ...defaults.promotionCard,
        ...(promotionCard || {}),
      },
    },
  } as TConfig
}

function normalizePromotionHubConfig(
  config?: UserPromotionHubDisplayPromotionConfig | null
): UserPromotionHubDisplayPromotionConfig {
  const settings = config?.settings || {}
  return {
    promotionType: 'promotion_hub_display',
    enabled: config?.enabled !== false,
    settings: {
      pageTitle: typeof settings.pageTitle === 'string' && settings.pageTitle.trim() ? settings.pageTitle : DEFAULT_PROMOTION_HUB_SETTINGS.pageTitle,
      pageSubtitle: typeof settings.pageSubtitle === 'string' && settings.pageSubtitle.trim() ? settings.pageSubtitle : DEFAULT_PROMOTION_HUB_SETTINGS.pageSubtitle,
      heroBadgeText: typeof settings.heroBadgeText === 'string' && settings.heroBadgeText.trim() ? settings.heroBadgeText : DEFAULT_PROMOTION_HUB_SETTINGS.heroBadgeText,
      warningTitle: typeof settings.warningTitle === 'string' && settings.warningTitle.trim() ? settings.warningTitle : DEFAULT_PROMOTION_HUB_SETTINGS.warningTitle,
      warningMessagePrimary:
        typeof settings.warningMessagePrimary === 'string' && settings.warningMessagePrimary.trim()
          ? settings.warningMessagePrimary
          : DEFAULT_PROMOTION_HUB_SETTINGS.warningMessagePrimary,
      warningMessageSecondary:
        typeof settings.warningMessageSecondary === 'string' && settings.warningMessageSecondary.trim()
          ? settings.warningMessageSecondary
          : DEFAULT_PROMOTION_HUB_SETTINGS.warningMessageSecondary,
      featuredSectionTitle:
        typeof settings.featuredSectionTitle === 'string' && settings.featuredSectionTitle.trim()
          ? settings.featuredSectionTitle
          : DEFAULT_PROMOTION_HUB_SETTINGS.featuredSectionTitle,
      featuredSectionSubtitle:
        typeof settings.featuredSectionSubtitle === 'string' && settings.featuredSectionSubtitle.trim()
          ? settings.featuredSectionSubtitle
          : DEFAULT_PROMOTION_HUB_SETTINGS.featuredSectionSubtitle,
      campaignsSectionTitle:
        typeof settings.campaignsSectionTitle === 'string' && settings.campaignsSectionTitle.trim()
          ? settings.campaignsSectionTitle
          : DEFAULT_PROMOTION_HUB_SETTINGS.campaignsSectionTitle,
      campaignsSectionSubtitle:
        typeof settings.campaignsSectionSubtitle === 'string' && settings.campaignsSectionSubtitle.trim()
          ? settings.campaignsSectionSubtitle
          : DEFAULT_PROMOTION_HUB_SETTINGS.campaignsSectionSubtitle,
      emptyCampaignText:
        typeof settings.emptyCampaignText === 'string' && settings.emptyCampaignText.trim()
          ? settings.emptyCampaignText
          : DEFAULT_PROMOTION_HUB_SETTINGS.emptyCampaignText,
      receiveButtonText:
        typeof settings.receiveButtonText === 'string' && settings.receiveButtonText.trim()
          ? settings.receiveButtonText
          : DEFAULT_PROMOTION_HUB_SETTINGS.receiveButtonText,
      showCampaigns:
        typeof settings.showCampaigns === 'boolean'
          ? settings.showCampaigns
          : DEFAULT_PROMOTION_HUB_SETTINGS.showCampaigns,
      disabledTitle:
        typeof settings.disabledTitle === 'string' && settings.disabledTitle.trim()
          ? settings.disabledTitle
          : DEFAULT_PROMOTION_HUB_SETTINGS.disabledTitle,
      disabledMessage:
        typeof settings.disabledMessage === 'string' && settings.disabledMessage.trim()
          ? settings.disabledMessage
          : DEFAULT_PROMOTION_HUB_SETTINGS.disabledMessage,
      sections: {
        warning: normalizeSection(settings.sections?.warning, DEFAULT_PROMOTION_HUB_SETTINGS.sections.warning),
        featuredPromotions: normalizeSection(
          settings.sections?.featuredPromotions,
          DEFAULT_PROMOTION_HUB_SETTINGS.sections.featuredPromotions
        ),
        shortcuts: normalizeSection(settings.sections?.shortcuts, DEFAULT_PROMOTION_HUB_SETTINGS.sections.shortcuts),
        campaigns: normalizeSection(
          settings.sections?.campaigns,
          {
            enabled:
              typeof settings.showCampaigns === 'boolean'
                ? settings.showCampaigns
                : DEFAULT_PROMOTION_HUB_SETTINGS.sections.campaigns.enabled,
            sortOrder: DEFAULT_PROMOTION_HUB_SETTINGS.sections.campaigns.sortOrder,
          }
        ),
      },
      manualCards: Array.isArray(settings.manualCards)
        ? settings.manualCards
            .map((card, index) => normalizeManualCard(card, DEFAULT_MANUAL_CARD(index + 1), index))
            .sort((left, right) => left.sortOrder - right.sortOrder || left.title.localeCompare(right.title))
        : DEFAULT_PROMOTION_HUB_SETTINGS.manualCards,
      shortcuts: {
        cashback: normalizeShortcut(settings.shortcuts?.cashback, DEFAULT_PROMOTION_HUB_SETTINGS.shortcuts.cashback),
        monthlyReward: normalizeShortcut(settings.shortcuts?.monthlyReward, DEFAULT_PROMOTION_HUB_SETTINGS.shortcuts.monthlyReward),
        referral: normalizeShortcut(settings.shortcuts?.referral, DEFAULT_PROMOTION_HUB_SETTINGS.shortcuts.referral),
        birthday: normalizeShortcut(settings.shortcuts?.birthday, DEFAULT_PROMOTION_HUB_SETTINGS.shortcuts.birthday),
        facebookShare: normalizeShortcut(settings.shortcuts?.facebookShare, DEFAULT_PROMOTION_HUB_SETTINGS.shortcuts.facebookShare),
      },
    },
    updatedAt: config?.updatedAt ?? null,
    updatedBy: config?.updatedBy ?? null,
  }
}

function isManualCardActive(card: UserPromotionHubManualCard): boolean {
  if (!card.enabled) return false
  const now = Date.now()
  const startsAt = card.startsAt ? new Date(card.startsAt).getTime() : null
  const endsAt = card.endsAt ? new Date(card.endsAt).getTime() : null
  if (startsAt && Number.isFinite(startsAt) && startsAt > now) return false
  if (endsAt && Number.isFinite(endsAt) && endsAt < now) return false
  return true
}

/** หน้าโปรโมชั่น — ให้เหมือน promotion.html เดิมมากขึ้น และให้แอดมินคุมการ์ดรูปได้เอง */
const ModernPromotionPage: React.FC<ModernPromotionPageProps> = ({ themeConfig, assetConfig }) => {
  const navigate = useNavigate()
  const themeFolder = useMemo(() => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue', [themeConfig.themeId])
  const [campaignCards, setCampaignCards] = useState<CampaignCardItem[]>([])
  const [loading, setLoading] = useState(true)
  const [applyingId, setApplyingId] = useState<string | null>(null)
  const [hubConfig, setHubConfig] = useState<UserPromotionHubDisplayPromotionConfig>(() => normalizePromotionHubConfig(null))
  const [cashbackDisplayConfig, setCashbackDisplayConfig] = useState<UserCashbackDisplayPromotionConfig>(() =>
    normalizeCashbackDisplayConfig(null)
  )
  const [referralDisplayConfig, setReferralDisplayConfig] = useState<UserReferralDisplayPromotionConfig>(() =>
    normalizeReferralDisplayConfig(null)
  )
  const [birthdayDisplayConfig, setBirthdayDisplayConfig] = useState<UserBirthdayDisplayPromotionConfig>(() =>
    normalizeManualReviewBonusDisplayConfig(DEFAULT_BIRTHDAY_DISPLAY_CONFIG, DEFAULT_BIRTHDAY_DISPLAY_SETTINGS)
  )
  const [facebookShareDisplayConfig, setFacebookShareDisplayConfig] = useState<UserFacebookShareDisplayPromotionConfig>(() =>
    normalizeManualReviewBonusDisplayConfig(
      DEFAULT_FACEBOOK_SHARE_DISPLAY_CONFIG,
      DEFAULT_FACEBOOK_SHARE_DISPLAY_SETTINGS
    )
  )
  const [monthlyRewardConfig, setMonthlyRewardConfig] = useState<UserMonthlyRewardPromotionConfig>(() =>
    normalizeMonthlyRewardConfig(DEFAULT_MONTHLY_REWARD_CONFIG)
  )

  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }

  const resolveImageUrl = (url: string): string => {
    if (!url) return getAssetUrl('/img/p1.jpg')
    if (url.startsWith('http://') || url.startsWith('https://')) return url
    return getAssetUrl(url.startsWith('/') ? url : `/${url}`)
  }

  const resolveCampaignImageUrl = (campaign: PromotionCampaignItem): string => {
    return resolveImageUrl(campaign.display?.imageUrl || campaign.display?.bannerUrl || '')
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      getPromotionDashboard().catch(() => [] as PromotionCampaignItem[]),
      getPromotionHubDisplayPromotionConfig(themeConfig.tenantId).catch(() => ({ success: false, data: undefined })),
      getCashbackDisplayPromotionConfig(themeConfig.tenantId).catch(() => ({ success: false, data: undefined })),
      getReferralDisplayPromotionConfig(themeConfig.tenantId).catch(() => ({ success: false, data: undefined })),
      getBirthdayDisplayPromotionConfig(themeConfig.tenantId).catch(() => ({ success: false, data: undefined })),
      getFacebookShareDisplayPromotionConfig(themeConfig.tenantId).catch(() => ({ success: false, data: undefined })),
      getMonthlyRewardPromotionConfig(themeConfig.tenantId).catch(() => ({ success: false, data: undefined })),
    ])
      .then(([list, hubRes, cashbackRes, referralRes, birthdayRes, facebookShareRes, monthlyRewardRes]) => {
        if (cancelled) return

        if (hubRes.success && hubRes.data) {
          setHubConfig(normalizePromotionHubConfig(hubRes.data))
        } else {
          setHubConfig(normalizePromotionHubConfig(null))
        }

        if (cashbackRes.success && cashbackRes.data) {
          setCashbackDisplayConfig(normalizeCashbackDisplayConfig(cashbackRes.data))
        } else {
          setCashbackDisplayConfig(normalizeCashbackDisplayConfig(null))
        }

        if (referralRes.success && referralRes.data) {
          setReferralDisplayConfig(normalizeReferralDisplayConfig(referralRes.data))
        } else {
          setReferralDisplayConfig(normalizeReferralDisplayConfig(null))
        }

        if (birthdayRes.success && birthdayRes.data) {
          setBirthdayDisplayConfig(
            normalizeManualReviewBonusDisplayConfig(birthdayRes.data, DEFAULT_BIRTHDAY_DISPLAY_SETTINGS)
          )
        } else {
          setBirthdayDisplayConfig(
            normalizeManualReviewBonusDisplayConfig(DEFAULT_BIRTHDAY_DISPLAY_CONFIG, DEFAULT_BIRTHDAY_DISPLAY_SETTINGS)
          )
        }

        if (facebookShareRes.success && facebookShareRes.data) {
          setFacebookShareDisplayConfig(
            normalizeManualReviewBonusDisplayConfig(
              facebookShareRes.data,
              DEFAULT_FACEBOOK_SHARE_DISPLAY_SETTINGS
            )
          )
        } else {
          setFacebookShareDisplayConfig(
            normalizeManualReviewBonusDisplayConfig(
              DEFAULT_FACEBOOK_SHARE_DISPLAY_CONFIG,
              DEFAULT_FACEBOOK_SHARE_DISPLAY_SETTINGS
            )
          )
        }

        if (monthlyRewardRes.success && monthlyRewardRes.data) {
          setMonthlyRewardConfig(normalizeMonthlyRewardConfig(monthlyRewardRes.data))
        } else {
          setMonthlyRewardConfig(normalizeMonthlyRewardConfig(DEFAULT_MONTHLY_REWARD_CONFIG))
        }

        if (Array.isArray(list) && list.length > 0) {
          const managedCampaignTypes = new Set(['cashback', 'referral', 'monthly_reward', 'birthday', 'facebook_share'])
          setCampaignCards(
            list
              .filter((campaign) => !managedCampaignTypes.has(String(campaign.type || '').trim().toLowerCase()))
              .map((campaign, index) => ({
                id: campaign.campaignId || campaign._id || `promo-${index}`,
                title: campaign.display?.title || campaign.name || 'โปรโมชั่น',
                description: campaign.display?.description || campaign.description || '',
                img: resolveCampaignImageUrl(campaign),
                buttonText: campaign.display?.buttonText || 'รับโบนัส',
                campaignId: campaign.campaignId || campaign._id || '',
              }))
              .sort((left, right) => left.title.localeCompare(right.title))
          )
        } else {
          setCampaignCards([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [themeFolder, assetConfig.CDN_BASE_URL, themeConfig.tenantId])

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget
    if (el.dataset.imgFallback === '1') return
    el.dataset.imgFallback = '1'
    el.src = IMG_PLACEHOLDER
  }

  const handleApplyCampaign = async (cardId: string, campaignId: string) => {
    if (!campaignId) {
      Swal.fire({ icon: 'info', title: 'แจ้งเตือน', text: 'โปรโมชั่นนี้ยังไม่เปิดรับในระบบ' })
      return
    }

    setApplyingId(cardId)
    try {
      const res = await applyPromotion(campaignId, 'campaign')
      if (res.success) {
        await Swal.fire({
          icon: 'success',
          title: 'รับโบนัสสำเร็จ',
          text: (res as { message?: string }).message || 'ระบบได้ลงทะเบียนโบนัสให้คุณแล้ว',
        })
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'ไม่สามารถรับได้',
          text: (res as { message?: string }).message || 'กรุณาตรวจสอบเงื่อนไขหรือติดต่อแอดมิน',
        })
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: msg })
    } finally {
      setApplyingId(null)
    }
  }

  const handleManualCardAction = async (card: UserPromotionHubManualCard) => {
    if (card.actionType === 'campaign') {
      await handleApplyCampaign(card.id, card.actionValue)
      return
    }

    if (card.actionType === 'route' && card.actionValue) {
      navigate(card.actionValue)
      return
    }

    if (card.actionType === 'url' && card.actionValue) {
      if (card.openInNewTab) {
        window.open(card.actionValue, '_blank', 'noopener,noreferrer')
      } else {
        window.location.href = card.actionValue
      }
      return
    }

    if (card.actionType === 'none') {
      Swal.fire({ icon: 'info', title: card.title || 'โปรโมชั่น', text: card.description || 'กรุณาติดต่อแอดมินเพื่อดูรายละเอียดเพิ่มเติม' })
    }
  }

  const handleCampaignCardAction = async (card: CampaignCardItem) => {
    if (card.actionType === 'route' && card.actionValue) {
      navigate(card.actionValue)
      return
    }

    await handleApplyCampaign(card.id, card.campaignId)
  }

  const hubSettings = hubConfig.settings as UserPromotionHubDisplaySettings
  const cashbackSettings = cashbackDisplayConfig.settings as UserCashbackDisplaySettings
  const cashbackPromotionCard = cashbackSettings.promotionCard
  const referralSettings = referralDisplayConfig.settings as UserReferralDisplaySettings
  const referralPromotionCard = referralSettings.promotionCard
  const monthlyRewardSettings = monthlyRewardConfig.settings?.display as UserMonthlyRewardDisplaySettings
  const monthlyRewardPromotionCard = monthlyRewardSettings.promotionCard
  const birthdaySettings = birthdayDisplayConfig.settings as UserManualReviewBonusDisplaySettings
  const birthdayPromotionCard = birthdaySettings.promotionCard
  const facebookShareSettings = facebookShareDisplayConfig.settings as UserManualReviewBonusDisplaySettings
  const facebookSharePromotionCard = facebookShareSettings.promotionCard

  const manualCards = hubSettings.manualCards
    .filter((card) => isManualCardActive(card))
    .sort((left, right) => left.sortOrder - right.sortOrder || left.title.localeCompare(right.title))

  const bonusShortcutSources: Array<ShortcutCardItem & { key: keyof UserPromotionHubDisplaySettings['shortcuts'] }> = [
    {
      key: 'cashback',
      ...hubSettings.shortcuts.cashback,
      enabled: cashbackDisplayConfig.enabled,
      badgeText: cashbackPromotionCard.badgeText || hubSettings.shortcuts.cashback.badgeText,
      title: cashbackPromotionCard.title || hubSettings.shortcuts.cashback.title,
      description: cashbackPromotionCard.description || hubSettings.shortcuts.cashback.description,
      buttonText: cashbackPromotionCard.buttonText || hubSettings.shortcuts.cashback.buttonText,
      route: cashbackSettings.promotionRoute || '/cashback',
      imageUrl: cashbackPromotionCard.imageUrl || '',
      imageAlt: cashbackPromotionCard.imageAlt || cashbackPromotionCard.title || hubSettings.shortcuts.cashback.title,
    },
    {
      key: 'monthlyReward',
      ...hubSettings.shortcuts.monthlyReward,
      enabled: monthlyRewardConfig.enabled,
      badgeText: monthlyRewardPromotionCard.badgeText || hubSettings.shortcuts.monthlyReward.badgeText,
      title: monthlyRewardPromotionCard.title || hubSettings.shortcuts.monthlyReward.title,
      description: monthlyRewardPromotionCard.description || hubSettings.shortcuts.monthlyReward.description,
      buttonText: monthlyRewardPromotionCard.buttonText || hubSettings.shortcuts.monthlyReward.buttonText,
      route: monthlyRewardPromotionCard.route || monthlyRewardSettings.promotionRoute || '/reward',
      imageUrl: monthlyRewardPromotionCard.imageUrl || '',
      imageAlt: monthlyRewardPromotionCard.imageAlt || monthlyRewardPromotionCard.title || hubSettings.shortcuts.monthlyReward.title,
    },
    {
      key: 'referral',
      ...hubSettings.shortcuts.referral,
      enabled: referralDisplayConfig.enabled,
      badgeText: referralPromotionCard.badgeText || hubSettings.shortcuts.referral.badgeText,
      title: referralPromotionCard.title || hubSettings.shortcuts.referral.title,
      description: referralPromotionCard.description || hubSettings.shortcuts.referral.description,
      buttonText: referralPromotionCard.buttonText || hubSettings.shortcuts.referral.buttonText,
      route: referralSettings.promotionRoute || '/affiliate',
      imageUrl: referralPromotionCard.imageUrl || '',
      imageAlt: referralPromotionCard.imageAlt || referralPromotionCard.title || hubSettings.shortcuts.referral.title,
    },
    {
      key: 'birthday',
      ...hubSettings.shortcuts.birthday,
      enabled: birthdayDisplayConfig.enabled,
      badgeText: birthdayPromotionCard.badgeText || hubSettings.shortcuts.birthday.badgeText,
      title: birthdayPromotionCard.title || hubSettings.shortcuts.birthday.title,
      description: birthdayPromotionCard.description || hubSettings.shortcuts.birthday.description,
      buttonText: birthdayPromotionCard.buttonText || hubSettings.shortcuts.birthday.buttonText,
      imageUrl: birthdayPromotionCard.imageUrl || '',
      imageAlt: birthdayPromotionCard.imageAlt || birthdayPromotionCard.title || hubSettings.shortcuts.birthday.title,
    },
    {
      key: 'facebookShare',
      ...hubSettings.shortcuts.facebookShare,
      enabled: facebookShareDisplayConfig.enabled,
      badgeText: facebookSharePromotionCard.badgeText || hubSettings.shortcuts.facebookShare.badgeText,
      title: facebookSharePromotionCard.title || hubSettings.shortcuts.facebookShare.title,
      description: facebookSharePromotionCard.description || hubSettings.shortcuts.facebookShare.description,
      buttonText: facebookSharePromotionCard.buttonText || hubSettings.shortcuts.facebookShare.buttonText,
      imageUrl: facebookSharePromotionCard.imageUrl || '',
      imageAlt:
        facebookSharePromotionCard.imageAlt ||
        facebookSharePromotionCard.title ||
        hubSettings.shortcuts.facebookShare.title,
    },
  ]

  const bonusShortcutCards: CampaignCardItem[] = bonusShortcutSources
    .filter((shortcut) => shortcut.enabled)
    .map((shortcut): CampaignCardItem => ({
      id: `shortcut-${shortcut.key}`,
      title: shortcut.title,
      description: shortcut.description,
      img: resolveImageUrl(shortcut.imageUrl || ''),
      buttonText: shortcut.buttonText,
      campaignId: '',
      actionType: 'route',
      actionValue: shortcut.route,
    }))
    .sort((left, right) => {
      const leftKey = left.id.replace('shortcut-', '') as keyof UserPromotionHubDisplaySettings['shortcuts']
      const rightKey = right.id.replace('shortcut-', '') as keyof UserPromotionHubDisplaySettings['shortcuts']
      return (
        hubSettings.shortcuts[leftKey].sortOrder - hubSettings.shortcuts[rightKey].sortOrder ||
        left.title.localeCompare(right.title)
      )
    })

  const displayedCampaignCards = [...bonusShortcutCards, ...campaignCards]

  const orderedSections = [
    {
      id: 'warning',
      enabled: hubSettings.sections.warning.enabled,
      sortOrder: hubSettings.sections.warning.sortOrder,
      content: (
        <div className="htd-promo__warn htd-promo-anim htd-promo-anim--2">
          <span className="htd-promo__warn-icon" aria-hidden>
            <i className="fas fa-exclamation-triangle" />
          </span>
          <div>
            <h3>{hubSettings.warningTitle}</h3>
            <p>{hubSettings.warningMessagePrimary}</p>
            <p>{hubSettings.warningMessageSecondary}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'featuredPromotions',
      enabled: hubSettings.sections.featuredPromotions.enabled && manualCards.length > 0,
      sortOrder: hubSettings.sections.featuredPromotions.sortOrder,
      content: (
        <section className="htd-promo-anim htd-promo-anim--3" aria-label={hubSettings.featuredSectionTitle}>
          <div className="htd-promo__section-head">
            <h2>{hubSettings.featuredSectionTitle}</h2>
            <p>{hubSettings.featuredSectionSubtitle}</p>
          </div>
          <div className="htd-promo__grid">
            {manualCards.map((card) => (
              <article key={card.id} className="htd-promo__card">
                <figure className="htd-promo__card-media">
                  <img
                    src={resolveImageUrl(card.imageUrl)}
                    alt={card.imageAlt || card.title || 'Promotion'}
                    onError={handleImgError}
                  />
                </figure>
                <div className="htd-promo__card-body">
                  {card.badgeText ? <span className="htd-promo__card-badge">{card.badgeText}</span> : null}
                  <h3 className="htd-promo__card-title">{card.title || 'โปรโมชั่น'}</h3>
                  <p className="htd-promo__card-desc">{card.description}</p>
                  <button
                    type="button"
                    className="htd-promo__card-btn"
                    onClick={() => void handleManualCardAction(card)}
                    disabled={applyingId === card.id}
                  >
                    {applyingId === card.id
                      ? 'กำลังดำเนินการ...'
                      : card.buttonText || (card.actionType === 'campaign' ? hubSettings.receiveButtonText : 'ดูรายละเอียด')}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ),
    },
    {
      id: 'campaigns',
      enabled: hubSettings.sections.campaigns.enabled,
      sortOrder: hubSettings.sections.campaigns.sortOrder,
      content: (
        <section className="htd-promo-anim htd-promo-anim--3" aria-label={hubSettings.campaignsSectionTitle}>
          <div className="htd-promo__section-head">
            <h2>{hubSettings.campaignsSectionTitle}</h2>
            <p>{hubSettings.campaignsSectionSubtitle}</p>
          </div>

          {displayedCampaignCards.length === 0 ? (
            <div className="htd-promo__empty">
              <p>{hubSettings.emptyCampaignText}</p>
            </div>
          ) : (
            <div className="htd-promo__grid">
              {displayedCampaignCards.map((card) => (
                <article key={card.id} className="htd-promo__card">
                  <figure className="htd-promo__card-media">
                    <img src={card.img} alt={card.title} onError={handleImgError} />
                  </figure>
                  <div className="htd-promo__card-body">
                    <h3 className="htd-promo__card-title">{card.title}</h3>
                    <p className="htd-promo__card-desc">{card.description}</p>
                    <button
                      type="button"
                      className="htd-promo__card-btn"
                      onClick={() => void handleCampaignCardAction(card)}
                      disabled={!!applyingId}
                    >
                      {applyingId === card.id ? 'กำลังดำเนินการ...' : card.buttonText || hubSettings.receiveButtonText}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      ),
    },
  ]
    .filter((section) => section.enabled)
    .sort((left, right) => left.sortOrder - right.sortOrder)

  return (
    <div className="content htd-promo">
      <div className="htd-promo__glow" aria-hidden />
      <div className="htd-promo__inner">
        <header className="htd-promo__hero htd-promo-anim htd-promo-anim--1">
          <div className="htd-promo__badge" aria-hidden>
            <i className="fas fa-gift" />
          </div>
          <div>
            <p className="htd-promo__eyebrow">Bonus Hub</p>
            <h1 className="htd-promo__title">{hubSettings.pageTitle || 'โปรโมชั่น'}</h1>
            <p className="htd-promo__desc">รวมโบนัสและสิทธิพิเศษที่เปิดให้รับอยู่ตอนนี้</p>
          </div>
        </header>

        {!hubConfig.enabled ? (
          <div className="htd-promo__shell">
            <h3>{hubSettings.disabledTitle}</h3>
            <p>{hubSettings.disabledMessage}</p>
          </div>
        ) : loading ? (
          <div className="htd-promo__loading">
            <div className="spinner-border" role="status" />
            <p className="mt-2 mb-0">กำลังโหลดโปรโมชั่น...</p>
          </div>
        ) : (
          orderedSections.map((section) => (
            <React.Fragment key={section.id}>{section.content}</React.Fragment>
          ))
        )}
      </div>
    </div>
  )
}

export default ModernPromotionPage
